<script setup>
import { ref, onMounted, watch } from "vue";
import { useField } from "vee-validate";
import * as yup from "yup";

const props = defineProps({
  formName: String,
  name: String,
  label: String,
  modelValue: {
    // Prop for v-model
    type: [String, Number, null], // Adjust type as needed
    default: "",
  },
  labelPosition: String,
  initialValue: String,
  dbFieldName: String, // database field name for mapping
  placeholder: String,
  description: String,
  max: Number,
  min: Number,
  required: {
    type: Boolean,
    default: false,
  },
});

// Define emits
const emit = defineEmits(["change", "update:modelValue"]);

// set up store location
const field = props.dbFieldName || props.name;

// Create a schema field based on the required prop
let schema = yup.string();

if (props.required) {
  schema = schema.required(`${props.label} is required.`);
}

// Vee-validate field setup
const { value, errorMessage, validate } = useField(props.name, schema, {
  initialValue: (() => {
    // Case 1: modelValue is empty (null or "") and a specific initialValue prop is provided.
    // In this case, the initialValue prop takes precedence to set the field's starting state.
    if (
      (props.modelValue === null || props.modelValue === "") &&
      props.initialValue
    ) {
      return props.initialValue;
    }
    // Case 2: modelValue is explicitly set (i.e., not null or undefined).
    // This includes scenarios where modelValue might be an empty string but initialValue wasn't applicable.
    if (props.modelValue !== null && props.modelValue !== undefined) {
      return props.modelValue;
    }
    // Case 3: If a placeholder is provided (and modelValue/initialValue didn't determine the value),
    // the field should start as an empty string to allow the disabled placeholder option to be shown.
    if (props.placeholder) {
      return "";
    }
    // Default fallback if none of the above conditions are met.
    return "";
  })(),
});

if (
  props.initialValue &&
  (props.modelValue === null || props.modelValue === "")
) {
  // If initialValue is provided AND the v-model (props.modelValue) is currently empty,
  // emit an update to the parent to set the v-model to this initialValue.
  // This ensures the parent's state reflects the intended starting value.
  emit("update:modelValue", props.initialValue);
}

function handleChange(event) {
  // value.value is already updated by v-model on the select element
  emit("update:modelValue", value.value); // Emit for v-model
  emit("change", value.value); // Emit for any other @change listeners
}

function capitalizeFirstLetter(string) {
  if (typeof string !== "string") return "";
  return string.charAt(0).toUpperCase() + string.slice(1);
}

// Watch for external changes to modelValue
watch(
  () => props.modelValue,
  (newValue) => {
    value.value = newValue;
  }
);

onMounted(() => {});
</script>

<template>
  <fieldset
    class="frm-field"
    :class="{ 'flex items-center gap-4': labelPosition === 'inline' }">
    <label :for="name" class="block mb-10">
      {{ label }} <span v-if="required" class="text-red-500">*</span>
    </label>
    <div class="frm-input">
      <select
        :id="name"
        :name="name"
        v-model="value"
        class="pr-20"
        @change="handleChange"
        @blur="validate">
        <option v-if="placeholder" value="" disabled>
          {{ placeholder }}
        </option>
        <option
          v-if="initialValue && (!placeholder || value === initialValue) && !$slots.options"
          :value="initialValue">
          {{ capitalizeFirstLetter(initialValue) }}
        </option>

        <slot name="options"></slot>
      </select>
    </div>
    <span
      v-if="errorMessage"
      class="block mt-10 text-14 text-error leading-none">
      {{ errorMessage }}
    </span>
    <span v-if="description" class="frm-desc">
      {{ description }}
    </span>
  </fieldset>
</template>
